import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
/**
 * ScientificNotationBuilderRenderer — Build a number in wetenschappelijke notatie.
 *
 * Didactic goal (GET-WETENSCHAPPELIJK / GET-WETENSCHAP-REKEN):
 * Make a × 10ⁿ tangible by separating the two choices the student must make:
 *   1. The coëfficiënt a (must satisfy 1 ≤ a < 10)
 *   2. The exponent n (0–8 for primary school range)
 *
 * The renderer shows the target value in standard notation and the student
 * composes a × 10^n that equals the target. Submission emits a JSON payload
 * { coefficient, exponent } which the validator checks against target +
 * canonical-form rules.
 *
 * Config (data.scientificNotationBuilder):
 *   - targetValue: number — the number to express in scientific notation
 *   - minExponent?: number (default 0)
 *   - maxExponent?: number (default 8)
 *   - allowedDecimals?: number (default 1) — how many decimal places on coef
 */
interface ScientificNotationBuilderConfig {
  targetValue: number;
  minExponent?: number;
  maxExponent?: number;
  allowedDecimals?: number;
}
const NL_NUMBER = new Intl.NumberFormat('nl-NL');
function formatTarget(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return NL_NUMBER.format(n);
}
function parseCoefInput(raw: string): number | null {
  if (!raw.trim()) return null;
  const normalized = raw.replace(',', '.').trim();
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const val = parseFloat(normalized);
  return Number.isFinite(val) ? val : null;
}
export const ScientificNotationBuilderRenderer: React.FC<QuestionRendererProps> = ({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}) => {
  const color = moduleColor || FALLBACK_COLOR;
  const config = (question.data as {
    scientificNotationBuilder?: ScientificNotationBuilderConfig;
  } | undefined)?.scientificNotationBuilder;
  const hasConfig = !!config && typeof config.targetValue === 'number';
  const minExp = config?.minExponent ?? 0;
  const maxExp = Math.max(minExp, config?.maxExponent ?? 8);
  const target = config?.targetValue ?? 0;
  const exponents = useMemo(() => {
    const arr: number[] = [];
    for (let e = minExp; e <= maxExp; e++) arr.push(e);
    return arr;
  }, [minExp, maxExp]);
  const [coefInput, setCoefInput] = useState<string>('');
  const [exponent, setExponent] = useState<number | null>(null);
  // Reset on question change
  useEffect(() => {
    setCoefInput('');
    setExponent(null);
  }, [question.id]);
  const coef = parseCoefInput(coefInput);
  const ready = coef !== null && exponent !== null;
  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);
  // Payload: JSON-encoded { coefficient, exponent }
  const payload = useMemo(() => {
    if (!ready) return '';
    return JSON.stringify({
      coefficient: coef,
      exponent
    });
  }, [coef, exponent, ready]);
  useEdgeTriggeredSubmit(payload, shouldValidate, onAnswer, question.id);
  if (!hasConfig) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-10829">
        <p className="text-amber-700 font-bold" data-id="element-10830">
          ⚠️ Configuratie ontbreekt (scientificNotationBuilder)
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-10831">
          Deze vraag mist{' '}
          <code data-id="element-10832">data.scientificNotationBuilder.targetValue</code>.
        </p>
      </div>;
  }
  const previewValue = coef !== null && exponent !== null ? Number((coef * Math.pow(10, exponent)).toPrecision(15)) : null;
  return <div className="flex flex-col items-center justify-center space-y-6 py-2" data-id="element-10833">
      {/* Target prompt */}
      <div className="text-center" data-id="element-10834">
        <p className="text-sm uppercase tracking-wide text-slate-500" data-id="element-10835">
          Schrijf in wetenschappelijke notatie
        </p>
        <p className="mt-2 font-bold leading-none" style={{
        fontSize: 'clamp(28px, 6vw, 44px)',
        color
      }} data-id="element-10836">
          {formatTarget(target)}
        </p>
      </div>

      {/* Expression: coef × 10^exp */}
      <div className="flex w-full max-w-[min(520px,95vw)] flex-wrap items-center justify-center gap-3" data-id="element-10837">
        {/* Coefficient input */}
        <div className="flex flex-col items-center" data-id="element-10838">
          <label htmlFor="snb-coef" className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500" data-id="element-10839">
            Coëfficiënt
          </label>
          <input id="snb-coef" type="text" inputMode="decimal" autoComplete="off" value={coefInput} onChange={e => setCoefInput(e.target.value)} placeholder="bv. 3,6" className="w-28 rounded-lg border-2 px-3 py-2 text-center text-2xl font-bold text-slate-800 focus:outline-none" style={{
          borderColor: coef !== null ? color : '#cbd5e1',
          backgroundColor: coef !== null ? hexToRgba(color, 0.06) : '#f8fafc'
        }} data-id="element-10840" />
          <span className="mt-1 text-[10px] text-slate-400" data-id="element-10841">1 ≤ a &lt; 10</span>
        </div>

        {/* × 10 */}
        <div className="flex items-center text-2xl font-bold text-slate-600" aria-hidden="true" data-id="element-10842">
          <span className="mx-1" data-id="element-10843">×</span>
          <span data-id="element-10844">10</span>
        </div>

        {/* Exponent picker */}
        <div className="flex flex-col items-center" data-id="element-10845">
          <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500" data-id="element-10846">
            Exponent
          </span>
          <div className="flex flex-wrap justify-center gap-1" role="radiogroup" aria-label="Kies de exponent" data-id="element-10847">
            {exponents.map(e => {
            const isActive = exponent === e;
            return <button key={e} type="button" role="radio" aria-checked={isActive} onClick={() => setExponent(e)} className="flex h-10 w-10 items-center justify-center rounded-md border-2 text-base font-bold transition-colors focus:outline-none" style={{
              borderColor: isActive ? color : '#cbd5e1',
              backgroundColor: isActive ? hexToRgba(color, 0.18) : '#ffffff',
              color: isActive ? color : '#475569'
            }} data-id="element-10848">
                  {e}
                </button>;
          })}
          </div>
          <span className="mt-1 text-[10px] text-slate-400" data-id="element-10849">
            {minExp}–{maxExp}
          </span>
        </div>
      </div>

      {/* Live preview */}
      <div className="min-h-[40px] flex items-center justify-center" data-id="element-10850">
        {ready && previewValue !== null && <motion.div key={`${coef}-${exponent}`} initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} className="rounded-full bg-white px-4 py-1.5 text-sm font-medium shadow-sm" style={{
        color,
        border: `1px solid ${hexToRgba(color, 0.3)}`
      }} data-id="element-10851">
            = {formatTarget(previewValue)}
          </motion.div>}
      </div>
    </div>;
};